import { AlertDialog as AlertDialogPrimitive } from "@base-ui/react/alert-dialog";
import * as React from "react";

import { Button } from "@beweegbaas/ui/components/ui/button";
import { cn, cnState } from "@beweegbaas/ui/lib/utils";

function AlertDialog({ ...props }: AlertDialogPrimitive.Root.Props) {
  return <AlertDialogPrimitive.Root data-slot="alert-dialog" {...props} />;
}

function AlertDialogTrigger({ ...props }: AlertDialogPrimitive.Trigger.Props) {
  return <AlertDialogPrimitive.Trigger data-slot="alert-dialog-trigger" {...props} />;
}

function AlertDialogPortal({ ...props }: AlertDialogPrimitive.Portal.Props) {
  return <AlertDialogPrimitive.Portal data-slot="alert-dialog-portal" {...props} />;
}

function AlertDialogOverlay({ className, ...props }: AlertDialogPrimitive.Backdrop.Props) {
  return (
    <AlertDialogPrimitive.Backdrop
      forceRender={true}
      data-slot="alert-dialog-overlay"
      className={cnState(
        `data-open:animate-in data-closed:animate-out data-closed:fade-out-0
        data-open:fade-in-0 fixed inset-0
        isolate z-50 bg-black/10 duration-100 supports-backdrop-filter:backdrop-blur-xs
        dark:bg-black/50`,
        className,
      )}
      {...props}
    />
  );
}

function AlertDialogContent({
  className,
  size = "default",
  ...props
}: AlertDialogPrimitive.Popup.Props & {
  size?: "default" | "sm";
}) {
  return (
    <AlertDialogPortal>
      <AlertDialogOverlay />
      <AlertDialogPrimitive.Popup
        data-slot="alert-dialog-content"
        data-size={size}
        className={cnState(
          `data-open:animate-in data-closed:animate-out data-closed:fade-out-0
          data-open:fade-in-0 data-closed:zoom-out-95 data-open:zoom-in-95
          bg-background ring-foreground/10 group/alert-dialog-content fixed top-1/2 left-1/2
          z-50 grid w-full
          -translate-x-1/2 -translate-y-1/2 gap-6
          rounded-xl p-6 ring-1 duration-100 outline-none data-[size=default]:max-w-xs data-[size=sm]:max-w-xs
          data-[size=default]:sm:max-w-lg`,
          className,
        )}
        {...props}
      />
    </AlertDialogPortal>
  );
}

function AlertDialogHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="alert-dialog-header"
      className={cn(
        `grid grid-rows-[auto_1fr] place-items-center gap-1.5 text-center
        has-data-[slot=alert-dialog-media]:grid-rows-[auto_auto_1fr]
        has-data-[slot=alert-dialog-media]:gap-x-6
        sm:group-data-[size=default]/alert-dialog-content:place-items-start
        sm:group-data-[size=default]/alert-dialog-content:text-left
        sm:group-data-[size=default]/alert-dialog-content:has-data-[slot=alert-dialog-media]:grid-rows-[auto_1fr]`,
        className,
      )}
      {...props}
    />
  );
}

function AlertDialogFooter({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="alert-dialog-footer"
      className={cn(
        `flex flex-col-reverse gap-2
        group-data-[size=sm]/alert-dialog-content:grid
        group-data-[size=sm]/alert-dialog-content:grid-cols-2 sm:flex-row
        sm:justify-end`,
        className,
      )}
      {...props}
    />
  );
}

function AlertDialogMedia({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="alert-dialog-media"
      className={cn(
        `bg-muted mb-2 inline-flex size-16 items-center justify-center
        rounded-md sm:group-data-[size=default]/alert-dialog-content:row-span-2
        *:[svg:not([class*='size-'])]:size-8`,
        className,
      )}
      {...props}
    />
  );
}

function AlertDialogTitle({
  className,
  ...props
}: React.ComponentProps<typeof AlertDialogPrimitive.Title>) {
  return (
    <AlertDialogPrimitive.Title
      data-slot="alert-dialog-title"
      className={cnState(
        `mb-0 text-lg
        sm:group-data-[size=default]/alert-dialog-content:group-has-data-[slot=alert-dialog-media]/alert-dialog-content:col-start-2`,
        className,
      )}
      {...props}
    />
  );
}

function AlertDialogDescription({
  className,
  ...props
}: React.ComponentProps<typeof AlertDialogPrimitive.Description>) {
  return (
    <AlertDialogPrimitive.Description
      data-slot="alert-dialog-description"
      className={cnState(
        `text-muted-foreground *:[a]:hover:not(.button):text-foreground *:[a]:not(.button):underline
        *:[a]:not(.button):underline-offset-3 text-sm text-balance
        md:text-pretty`,
        className,
      )}
      {...props}
    />
  );
}

function AlertDialogAction({ className, ...props }: React.ComponentProps<typeof Button>) {
  return <Button data-slot="alert-dialog-action" className={cn(className)} {...props} />;
}

function AlertDialogCancel({
  className,
  variant = "outline",
  size = "default",
  ...props
}: AlertDialogPrimitive.Close.Props &
  Pick<React.ComponentProps<typeof Button>, "variant" | "size">) {
  return (
    <AlertDialogPrimitive.Close
      data-slot="alert-dialog-cancel"
      className={className}
      render={<Button variant={variant} size={size} />}
      {...props}
    />
  );
}

export {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogMedia,
  AlertDialogOverlay,
  AlertDialogPortal,
  AlertDialogPrimitive,
  AlertDialogTitle,
  AlertDialogTrigger,
};
